import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import { setCarDeliveryConfig, setTempCarDeliveryConfig } from "../redux/actions/UscCarDeliveryActions";
import { getCarDeliveryFromStorage, getSessionStorageParams } from "../utils/sessionStorage";
import { setNotification } from "../../../common-deprecated/redux/actions/NotificationActions";
import type { UscCarDeliveryConfig } from "../utils/carDeliveryUtils";
import { type UscDispatchType, useUscSelector } from "../redux/store";
import { useUscLabel } from "../utils/labels";
import { useDispatch } from "../../../shared-logic/redux/store";

const useUscCarDeliveryInit = (isActive: boolean): void => {
    const dispatch = useDispatch<UscDispatchType>();
    const carDelivery = useUscSelector((state) => state.carDelivery);
    const sessionStorageParams = useUscSelector(getSessionStorageParams, shallowEqual);
    const dealerId = useUscSelector((state) => state.dealer.dealer?.id);
    const activeFinanceOption = useUscSelector((state) => state.basket.activeFinanceOption);
    const [notificationTitle, notificationDescription] = useUscLabel([
        "uscCarDeliveryNotificationMonthlyTitle",
        "uscCarDeliveryNotificationMonthlyDescription",
    ]);

    // When the active finance option changes from cash -> monthly, we remove the car delivery config
    // from the basket. This is because the car delivery config is only relevant for cash purchases.
    useEffect(() => {
        if (carDelivery.deliveryConfig && isActive && activeFinanceOption !== "cash") {
            dispatch(
                setNotification({
                    title: notificationTitle,
                    description: notificationDescription,
                }),
            );
            dispatch(setTempCarDeliveryConfig(carDelivery.deliveryConfig));
            dispatch(setCarDeliveryConfig(null));
        }

        // If the active finance option changes from monthly -> cash, we set the car delivery config
        // to the temporary car delivery config.
        if (carDelivery.tempDeliveryConfig && isActive && activeFinanceOption === "cash") {
            dispatch(setTempCarDeliveryConfig(null));
            dispatch(setCarDeliveryConfig(carDelivery.tempDeliveryConfig as UscCarDeliveryConfig));
        }
    }, [activeFinanceOption]);

    useEffect(() => {
        if (!dealerId) return;

        const deliveryConfigSession = getCarDeliveryFromStorage(sessionStorageParams);

        if (deliveryConfigSession && !carDelivery.deliveryConfig) {
            // No delivery config in redux, but there is one in session storage.
            // Check if the dealer id matches the one in session storage and if so, set the delivery config in redux.
            // We also check if the active finance option is not monthly, because
            // car delivery is disabled for monthly finance.
            if (deliveryConfigSession[dealerId] && activeFinanceOption !== "monthly") {
                dispatch(setCarDeliveryConfig(deliveryConfigSession[dealerId]));
            }
        }
    }, [dealerId]);
};

export default useUscCarDeliveryInit;
